/**
 * PCCartStore — single source of truth for ALL Shopify cart reads.
 *
 * Root causes addressed:
 *  1. theme.js calls Shopify.getCart() (XHR) 3-5× on page load and 2× per
 *     mini-cart quantity change — all independent, no shared state.
 *  2. Third-party app embeds call fetch('/cart.json') independently, adding
 *     7+ more concurrent requests per page load.
 *  3. The SpurIT app (Split Partial Payments — spurit.global-2.x.min.js,
 *     k.requestCart → k.ajax) polls GET /cart.js?hash=<random> over raw
 *     XMLHttpRequest every 4 seconds on EVERY page, forever (measured on the
 *     preview theme: ~11 requests in 45 s on an idle page). Those reads never
 *     touched window.fetch, so nothing here could absorb them, and a shopper's
 *     "Complete Purchase" landed on top of that traffic and was intermittently
 *     answered 429 Too Many Requests.
 *
 *     NOTE FOR SUPPORT: we cannot change SpurIT's code. Layer 4 below serves
 *     its polls from this cache, but SpurIT support should still be asked to
 *     turn the 4 s cart polling off, or limit it to the product and cart pages.
 *
 * Solution — interception at every transport:
 *  Layer 1: window.fetch patch — catches ALL fetch() calls to /cart.js and
 *    /cart.json regardless of source (theme, app embeds, third-party scripts).
 *    Concurrent callers share one in-flight promise → one HTTP request max.
 *    Write operations (/cart/add|change|update|clear) go out one at a time and
 *    their responses keep the cache fresh before the caller sees them.
 *  Layer 2: Shopify.getCart patch — api.jquery.js uses jQuery XHR (not fetch),
 *    which bypasses Layer 1. Redirect it through the store directly.
 *  Layer 3: jQuery cart writes (dataFilter) — see below.
 *  Layer 4: XMLHttpRequest patch — GET /cart.js|json over raw XHR (SpurIT,
 *    jQuery $.getJSON) is answered from the same cache with a fake-completed
 *    XHR; XHR writes are tracked exactly like fetch writes.
 *
 * Cache rules (shared by every layer):
 *  - A cached cart is served for up to MAX_AGE_MS (60 s); after that the next
 *    read goes to the network once and every concurrent reader shares it.
 *  - Every write bumps an epoch when it starts and when it ends. A network
 *    read that started under an older epoch is still handed to its callers
 *    but never cached, so a read racing a write cannot resurrect the old cart.
 *  - While a write is in flight (isWriting()), readers are given the cached
 *    cart even if it is past MAX_AGE_MS rather than adding requests on top of
 *    the write. beginWrite() lets a long flow (the ring builder's Complete
 *    Purchase) hold that state for its whole duration.
 *  - Returning to the tab (visibilitychange / focus after ≥3 s away) or a
 *    bfcache restore drops the cache, so a cart changed in another tab is
 *    re-read.
 *  - Anything unexpected falls back to the real network request.
 *
 * Debugging: add ?pcCartDebug=1 to any URL (sticks for the tab session,
 * ?pcCartDebug=0 turns it off) to log every read and write, and call
 * window.PCCartStore.stats() in the console for counters.
 *
 * Zero functionality removed. Every original feature still works — the store
 * only changes WHERE the data comes from, not WHAT is returned.
 */
window.PCCartStore = (function () {

  // Save the real fetch FIRST, before we replace it.
  // All internal fetches must use _realFetch to avoid infinite recursion.
  var _realFetch = window.fetch.bind(window);

  var MAX_AGE_MS     = 60000; // serve a cached cart for at most a minute
  var RETURN_AWAY_MS = 3000;  // tab away at least this long → re-read on return
  var WRITE_MAX_MS   = 45000; // a beginWrite() that is never ended expires
  var WRITE_SLOT_MS  = 10000; // a hung write stops blocking the queue after this

  var _state     = null; // last known full cart JSON object
  var _fetchedAt = 0;    // when _state was obtained
  var _inflight  = null; // in-flight Promise<cart> shared by concurrent callers
  var _inflightEpoch = -1;
  var _epoch     = 0;    // bumped at the start and end of every write
  var _writing   = 0;    // writes (and beginWrite holders) currently open
  var _writeQueue = Promise.resolve();

  var _stats = {
    since: Date.now(),
    network: 0,       // real GET /cart.js requests made by the store
    served: 0,        // reads answered from the cache
    joined: 0,        // reads that shared an in-flight request
    xhrServed: 0,     // of those, raw XHR reads (SpurIT, $.getJSON)
    xhrFallback: 0,   // XHR reads sent to the network because the store failed
    writes: 0,        // cart writes seen (fetch + XHR + beginWrite holders)
    invalidations: 0,
    networkTimes: []  // timestamps of the last 50 network reads
  };

  // ── Debug logging ──────────────────────────────────────────────────────────
  var _debug = false;
  try {
    var _dbgMatch = /[?&]pcCartDebug=([01])/.exec(window.location.search);
    if (_dbgMatch) sessionStorage.setItem('pcCartDebug', _dbgMatch[1]);
    _debug = sessionStorage.getItem('pcCartDebug') === '1';
  } catch (e) { /* storage blocked — debug stays off */ }

  function _log() {
    if (!_debug || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[PCCartStore]');
    console.info.apply(console, args);
  }

  // ── Reads ──────────────────────────────────────────────────────────────────

  function _fetchFresh(source) {
    var epoch = _epoch;
    _stats.network++;
    _stats.networkTimes.push(Date.now());
    if (_stats.networkTimes.length > 50) _stats.networkTimes.shift();
    _log('network GET /cart.js for', source || 'direct');

    var p = _realFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('PCCartStore: /cart.js ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (_inflight === p) _inflight = null;
        // A write started or finished while this was in flight: hand the
        // result to whoever asked, but do not cache what may be the old cart.
        if (epoch === _epoch) {
          _state     = cart;
          _fetchedAt = Date.now();
        }
        return cart;
      }, function (err) {
        if (_inflight === p) _inflight = null;
        throw err;
      });

    _inflight = p;
    _inflightEpoch = epoch;
    return p;
  }

  function _read(source) {
    if (_state && (_writing > 0 || Date.now() - _fetchedAt < MAX_AGE_MS)) {
      _stats.served++;
      _log('cache hit for', source);
      return Promise.resolve(_state);
    }
    if (_inflight && _inflightEpoch === _epoch) {
      _stats.joined++;
      return _inflight;
    }
    return _fetchFresh(source);
  }

  function _invalidate(reason) {
    _state = null;
    _stats.invalidations++;
    _log('invalidate:', reason || 'manual');
  }

  // ── Writes ─────────────────────────────────────────────────────────────────

  // Returns an idempotent end() function. Bumps the epoch at both ends.
  function _beginWrite(label) {
    var ended = false;
    _writing++;
    _epoch++;
    _stats.writes++;
    _log('write start:', label);
    var timer = setTimeout(end, WRITE_MAX_MS);
    function end() {
      if (ended) return;
      ended = true;
      clearTimeout(timer);
      _writing = Math.max(0, _writing - 1);
      _epoch++;
      _log('write end:', label);
    }
    return end;
  }

  // Cart writes go out one at a time. Bursts of parallel writes (gift
  // reconcilers, app embeds, the delivery-date writer all firing on load) are
  // what push Shopify into 429s, and two writes addressing lines by key race
  // each other. A write that hangs stops blocking the next after WRITE_SLOT_MS.
  function _enqueueWrite(run) {
    var p = _writeQueue.then(run);
    var settled = p.then(function () {}, function () {});
    _writeQueue = Promise.race([
      settled,
      new Promise(function (resolve) { setTimeout(resolve, WRITE_SLOT_MS); })
    ]);
    return p;
  }

  // What a finished write means for the cache. /cart/change.js, update.js and
  // clear.js answer with the whole cart, which is adopted outright. add.js
  // answers with just the added line(s), so the next read re-fetches. A 4xx
  // left the cart as it was, so the cache stays. Anything else invalidates.
  function _settleWrite(status, bodyText, label) {
    if (status >= 400 && status < 500) return;
    if (status >= 200 && status < 300 && typeof bodyText === 'string') {
      try {
        if (store.set(JSON.parse(bodyText))) return;
      } catch (e) { /* not JSON — fall through */ }
    }
    _invalidate(label);
  }

  var store = {
    // Return cached cart or join/start a single in-flight fetch.
    get: function () {
      return _read('direct');
    },
    // Write a known-fresh cart (e.g. from a /cart/change.js response body).
    //
    // item_count is part of the test, not decoration: a BATCH /cart/add.js
    // answers `{items: [...]}` — the lines that were added, with no totals and
    // no other line in the cart. That shape passes an items-array check while
    // being the opposite of a cart, and caching it left every reader seeing a
    // bag holding only whatever was last added. Callers must invalidate() when
    // this returns false.
    set: function (cart) {
      if (cart && typeof cart === 'object' &&
          Array.isArray(cart.items) && typeof cart.item_count === 'number') {
        _state     = cart;
        _fetchedAt = Date.now();
        _inflight  = null;
        _epoch++; // an older in-flight read must not overwrite this
        return true;
      }
      return false;
    },
    // Mark cache stale; next get() fetches fresh (concurrent callers share one request).
    invalidate: function () {
      _invalidate('invalidate()');
    },
    // Force a brand-new fetch regardless of cache state.
    refresh: function () {
      _state    = null;
      _inflight = null;
      return _fetchFresh('refresh()');
    },
    // Hold the "writing" state across a multi-step flow (create product →
    // wait → cart/add). Readers get the cached cart meanwhile instead of
    // adding requests on top of it. Returns an end() function; always call it.
    beginWrite: function (label) {
      return _beginWrite(label || 'beginWrite()');
    },
    isWriting: function () {
      return _writing > 0;
    },
    stats: function () {
      var now = Date.now();
      var lastMinute = _stats.networkTimes.filter(function (t) { return now - t < 60000; }).length;
      return {
        sinceSeconds: Math.round((now - _stats.since) / 1000),
        network: _stats.network,
        networkLastMinute: lastMinute,
        served: _stats.served,
        joined: _stats.joined,
        xhrServed: _stats.xhrServed,
        xhrFallback: _stats.xhrFallback,
        writes: _stats.writes,
        invalidations: _stats.invalidations,
        writing: _writing,
        cacheAgeSeconds: _state ? Math.round((now - _fetchedAt) / 1000) : null
      };
    },
    debug: function (on) {
      _debug = !!on;
      try { sessionStorage.setItem('pcCartDebug', on ? '1' : '0'); } catch (e) {}
      return _debug;
    }
  };

  // ── Layer 1: window.fetch interception ─────────────────────────────────────
  // Catches fetch('/cart.js'), fetch('/cart.json'), and absolute-URL variants
  // from ANY script on the page — theme or third-party app embed.

  function _parseRequest(url, options) {
    try {
      var urlStr = (url instanceof Request) ? url.url
                 : (url instanceof URL)     ? url.href
                 :                            String(url);

      // Extract pathname — works for both '/cart.js' and 'https://…/cart.js'
      var pathname = (urlStr.indexOf('//') !== -1)
                   ? new URL(urlStr).pathname
                   : urlStr.split('?')[0].split('#')[0];

      var method = 'GET';
      if (options && options.method)        method = options.method.toUpperCase();
      else if (url instanceof Request)      method = url.method.toUpperCase();

      return { pathname: pathname, method: method };
    } catch (e) {
      return { pathname: '', method: 'GET' };
    }
  }

  function _isCartRead(p) {
    return p.endsWith('/cart.js') || p.endsWith('/cart.json');
  }

  // clear.js is a write too: missing it left the pre-clear cart cached, so the
  // ring builder's "Replace" flow read the old diamond back after clearing.
  var CART_WRITE_PATH = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;

  window.fetch = function (url, options) {
    var args = arguments;
    var req = _parseRequest(url, options);
    var p   = req.pathname;
    var m   = req.method;

    // ── Intercept cart READS (GET /cart.js or /cart.json, with locale prefix support)
    if (m === 'GET' && _isCartRead(p) && typeof Response !== 'undefined') {
      return _read('fetch').then(function (cart) {
        // Return a synthetic Response so the caller's .then(r => r.json()) still works.
        return new Response(JSON.stringify(cart), {
          status:  200,
          headers: { 'Content-Type': 'application/json' }
        });
      }, function () {
        // The store could not read the cart: let the caller's own request go.
        return _realFetch.apply(window, args);
      });
    }

    // ── Cart WRITES: one at a time; the cache is settled from the response
    // BEFORE the caller's .then() runs, so its follow-up reads see the new cart.
    if (m === 'POST' && CART_WRITE_PATH.test(p)) {
      var end = _beginWrite('fetch ' + p);
      return _enqueueWrite(function () { return _realFetch.apply(window, args); })
        .then(function (response) {
          return response.clone().text()
            .then(function (text) { _settleWrite(response.status, text, 'fetch ' + p); },
                  function () { _settleWrite(response.status, null, 'fetch ' + p); })
            .then(function () { end(); return response; });
        }, function (err) {
          _invalidate('fetch write failed ' + p);
          end();
          throw err;
        });
    }

    // All other requests pass through untouched.
    return _realFetch.apply(window, args);
  };

  // ── Layer 2: Shopify.getCart patch ─────────────────────────────────────────
  // api.jquery.js defines Shopify.getCart as jQuery.getJSON('/cart.js', cb).
  // jQuery uses XHR (not fetch), so it bypasses Layer 1 above.
  // Redirect it through the store so XHR-based callers also deduplicate.
  (function patchShopifyGetCart() {
    if (typeof window.Shopify === 'undefined' ||
        typeof window.Shopify.getCart !== 'function') return;

    var _orig = window.Shopify.getCart;
    window.Shopify.getCart = function (callback) {
      _read('Shopify.getCart')
        .then(function (cart) {
          if (typeof callback === 'function') callback(cart);
        })
        .catch(function () {
          // Error fallback: let the original XHR call through.
          _orig.call(window.Shopify, callback);
        });
    };
  })();

  // ── Layer 3: jQuery cart WRITES ────────────────────────────────────────────
  //
  // Layer 1 keeps the cache honest for writes that go out on fetch. The
  // theme's own adds do not go out on fetch: AjaxCart (every product form) and
  // the quickview modal post /cart/add.js over jQuery XHR, which never reaches
  // the fetch patch. So the cache still held the PRE-add cart while their
  // success handlers were already redrawing the mini-cart from it — the drawer
  // sliding open without the item that had just been added, and staying that
  // way until a reload refilled the cache from a fresh page.
  //
  // The hook has to be dataFilter, not ajaxComplete. jQuery runs a request's
  // own success callbacks BEFORE global ajaxComplete handlers, and success is
  // exactly where theme.js calls miniCart.updateElements() and generateCart()
  // — so invalidating from ajaxComplete (as pc-duplicate-guard.js does, for
  // its own snapshot) is always one step too late to help them. dataFilter
  // runs while the response is still being converted, ahead of all of it.
  // (Layer 4 now settles the same responses at readyState 4, which is also
  // ahead of jQuery's callbacks; this stays as a second line of defence.)
  function patchJqueryCartWrites($) {
    $.ajaxPrefilter(function (options) {
      var pathname = _parseRequest(options.url, null).pathname;
      if (!CART_WRITE_PATH.test(pathname)) return;

      // Chain rather than replace: leave any dataFilter the caller set alone.
      var _prev = options.dataFilter;
      options.dataFilter = function (data, type) {
        try {
          if (!store.set(JSON.parse(data))) store.invalidate();
        } catch (e) {
          store.invalidate();
        }
        return _prev ? _prev(data, type) : data;
      };
    });
  }

  if (window.jQuery) {
    patchJqueryCartWrites(window.jQuery);
  } else {
    // api.jquery.js and theme.js are both deferred, so a jQuery that is not
    // here yet is here by DOMContentLoaded — and no cart write can have gone
    // out in between, because nothing is bound to click yet.
    document.addEventListener('DOMContentLoaded', function () {
      if (window.jQuery) patchJqueryCartWrites(window.jQuery);
    });
  }

  // ── Layer 4: raw XMLHttpRequest ────────────────────────────────────────────
  //
  // GET /cart.js|json: answered from the store. The real XHR is opened (so
  // setRequestHeader etc. keep working) but never sent; the instance is given
  // own readyState/status/response* properties that shadow the prototype's
  // getters, and readystatechange → load → loadend are dispatched on it, which
  // reaches both on* handlers and addEventListener listeners (jQuery 1.x/2.x
  // and SpurIT's k.ajax read exactly these). Sync XHRs and responseTypes other
  // than ''/'text'/'json' are left alone. If the store fails, the real request
  // is sent instead.
  //
  // POST /cart/(add|change|update|clear): tracked like a fetch write (not
  // queued — holding an XHR's send() back would outlast callers' timeouts).
  (function patchXhr() {
    var XHR = window.XMLHttpRequest;
    if (!XHR || !XHR.prototype || typeof Object.defineProperty !== 'function') return;
    var proto  = XHR.prototype;
    var _open  = proto.open;
    var _send  = proto.send;
    var _abort = proto.abort;
    var FAKE_KEYS = ['readyState', 'status', 'statusText', 'responseText', 'response',
                     'responseURL', 'responseXML', 'getResponseHeader', 'getAllResponseHeaders'];

    function absUrlOf(url) {
      try { return new URL(String(url), window.location.href); } catch (e) { return null; }
    }

    function define(xhr, key, value) {
      Object.defineProperty(xhr, key, { configurable: true, enumerable: true, writable: true, value: value });
    }

    // Drop the shadowing properties so a reused XHR reports its real state.
    function clearFake(xhr) {
      for (var i = 0; i < FAKE_KEYS.length; i++) {
        if (Object.prototype.hasOwnProperty.call(xhr, FAKE_KEYS[i])) delete xhr[FAKE_KEYS[i]];
      }
    }

    function fire(xhr, type, size) {
      var ev;
      try {
        ev = (type === 'readystatechange' || typeof ProgressEvent !== 'function')
          ? new Event(type)
          : new ProgressEvent(type, { lengthComputable: true, loaded: size, total: size });
      } catch (e) {
        ev = document.createEvent('Event');
        ev.initEvent(type, false, false);
      }
      xhr.dispatchEvent(ev); // listener exceptions are reported, never thrown here
    }

    function complete(xhr, info, cart) {
      var text = JSON.stringify(cart);
      var contentType = 'application/json; charset=utf-8';
      define(xhr, 'readyState', 4);
      define(xhr, 'status', 200);
      define(xhr, 'statusText', 'OK');
      define(xhr, 'responseText', text);
      define(xhr, 'response', xhr.responseType === 'json' ? JSON.parse(text) : text);
      define(xhr, 'responseURL', info.absUrl);
      define(xhr, 'responseXML', null);
      define(xhr, 'getResponseHeader', function (name) {
        return String(name).toLowerCase() === 'content-type' ? contentType : null;
      });
      define(xhr, 'getAllResponseHeaders', function () {
        return 'content-type: ' + contentType + '\r\n';
      });
      fire(xhr, 'readystatechange');
      fire(xhr, 'load', text.length);
      fire(xhr, 'loadend', text.length);
    }

    function serveRead(xhr, info, body) {
      info.pending = true;
      _read('xhr').then(function (cart) {
        if (!info.pending || xhr.__pcCart !== info) return;
        info.pending = false;
        _stats.xhrServed++;
        complete(xhr, info, cart);
      }, function () {
        if (!info.pending || xhr.__pcCart !== info) return;
        info.pending = false;
        _stats.xhrFallback++;
        _log('XHR read fell back to the network');
        try { _send.call(xhr, body); } catch (e) { /* nothing more we can do */ }
      });
    }

    function trackWrite(xhr, info) {
      var end = _beginWrite('xhr ' + info.path);
      var done = false;
      function settle() {
        if (done || xhr.readyState !== 4) return;
        done = true;
        var text = null;
        try {
          if (!xhr.responseType || xhr.responseType === 'text') text = xhr.responseText;
        } catch (e) { /* responseText unreadable — invalidate below */ }
        _settleWrite(xhr.status || 0, text, 'xhr ' + info.path);
        end();
      }
      // readystatechange(4) fires before load, i.e. before jQuery's success
      // callbacks, so they already read the new cart. loadend is the backstop.
      xhr.addEventListener('readystatechange', settle);
      xhr.addEventListener('loadend', function () {
        if (!done) { done = true; _invalidate('xhr write ended'); end(); }
      });
    }

    var ourOpen = function (method, url, async) {
      try {
        if (this.__pcCart) this.__pcCart.pending = false; // reused mid-flight
        clearFake(this);
        var abs = absUrlOf(url);
        this.__pcCart = {
          method: String(method || 'GET').toUpperCase(),
          // Same-origin only; another host's /cart.js is not this cart.
          path: abs && abs.origin === window.location.origin ? abs.pathname : '',
          absUrl: abs ? abs.href : String(url),
          async: arguments.length < 3 || async !== false,
          pending: false
        };
      } catch (e) {
        this.__pcCart = null;
      }
      return _open.apply(this, arguments);
    };

    var ourSend = function (body) {
      var info = this.__pcCart;
      if (info && info.path) {
        try {
          var rt = this.responseType;
          if (info.method === 'GET' && info.async && _isCartRead(info.path) &&
              (!rt || rt === 'text' || rt === 'json')) {
            serveRead(this, info, body);
            return;
          }
          if (info.method === 'POST' && CART_WRITE_PATH.test(info.path)) trackWrite(this, info);
        } catch (e) {
          info.pending = false; // fall through to the real request
        }
      }
      return _send.apply(this, arguments);
    };

    var ourAbort = function () {
      var info = this.__pcCart;
      if (info && info.pending) {
        // A real in-flight abort ends in readyState 4 with status 0 and fires
        // readystatechange → abort → loadend, then returns to UNSENT.
        info.pending = false;
        define(this, 'readyState', 4);
        define(this, 'status', 0);
        define(this, 'statusText', '');
        fire(this, 'readystatechange');
        fire(this, 'abort', 0);
        fire(this, 'loadend', 0);
        define(this, 'readyState', 0);
      }
      return _abort.apply(this, arguments);
    };

    try {
      proto.open = ourOpen;
      proto.send = ourSend;
      proto.abort = ourAbort;
      if (proto.open !== ourOpen || proto.send !== ourSend || proto.abort !== ourAbort) {
        // Someone made these read-only: put back whatever stuck and stand down.
        proto.open = _open; proto.send = _send; proto.abort = _abort;
        _log('XHR patch not installed');
      }
    } catch (e) {
      try { proto.open = _open; proto.send = _send; proto.abort = _abort; } catch (e2) { /* leave as is */ }
    }
  })();

  // ── Re-read after the shopper comes back ───────────────────────────────────
  // The cart may have changed in another tab. Short blurs (clicking into an
  // iframe and out again) are ignored.
  (function invalidateOnReturn() {
    var awaySince = 0;
    function away() { if (!awaySince) awaySince = Date.now(); }
    function back(reason) {
      if (awaySince && Date.now() - awaySince >= RETURN_AWAY_MS && _writing === 0) {
        _invalidate(reason);
      }
      awaySince = 0;
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') away();
      else back('tab visible');
    });
    window.addEventListener('blur', away);
    window.addEventListener('focus', function () { back('window focus'); });
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) _invalidate('bfcache restore');
    });
  })();

  return store;

}());
